import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, MessageCircle, Flag, Trash2, Edit3, Store, User as UserIcon, Check, RefreshCcw, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { formatPrice, CATEGORY_META } from "./Marketplace";
import { AuctionPanel, MakeOfferButton, ShippingQuoteWidget } from "@/components/AuctionAndOfferPanels";

const CONDITION_LABEL = {
  new: "New — sealed / tags on",
  like_new: "Used — like new",
  good: "Used — good",
  fair: "Used — fair",
  for_parts: "For parts",
};

const DELIVERY_LABEL = {
  pickup: "Pickup only",
  shipping: "Ship only",
  both: "Pickup or ship",
};

export default function MarketplaceListing() {
  const { lid } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [l, setL] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/marketplace/listings/${lid}`);
        if (alive) setL(r.data);
      } catch (e) { if (alive) toast.error(formatApiError(e.response?.data?.detail)); }
    })();
    return () => { alive = false; };
  }, [lid]);

  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/marketplace/config");
        setPaymentsEnabled(!!r.data?.payments_enabled);
      } catch { /* payments stay disabled */ }
    })();
  }, []);

  if (!l) return <div className="max-w-4xl mx-auto px-6 py-16 text-gray-500">Loading…</div>;

  const isMine = user?.id === l.seller_id;

  const contact = async () => {
    if (!user) { nav("/login"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/marketplace/listings/${lid}/contact`);
      nav(`/messages?thread=${r.data.thread_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const buyNow = async () => {
    if (!user) { nav("/login"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/marketplace/listings/${lid}/buy`, {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  const markSold = async () => {
    if (!confirm("Mark as sold?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/marketplace/listings/${lid}/mark-sold`);
      setL(r.data.listing);
      toast.success("Marked as sold");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const relist = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/marketplace/listings/${lid}/relist`);
      setL(r.data.listing);
      toast.success("Relisted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!confirm("Delete this listing? This can't be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/marketplace/listings/${lid}`);
      toast.success("Deleted");
      nav("/marketplace/my");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const flag = async () => {
    const reason = prompt("Why are you reporting this listing? (optional)") || "";
    setBusy(true);
    try {
      const r = await api.post(`/marketplace/listings/${lid}/flag`, { reason });
      if (r.data?.hidden) { toast.success("Reported and auto-hidden."); nav("/marketplace"); }
      else toast.success("Reported. Thanks.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const photos = l.photos && l.photos.length ? l.photos : [];
  const videos = l.videos && l.videos.length ? l.videos : [];

  // Render the right kind of embed for a given video URL. YouTube + Vimeo
  // get iframe embeds so autoplay/native player work; anything else (esp.
  // direct .mp4 URLs) gets an HTML5 <video controls> element.
  const renderVideo = (url) => {
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, "");
      if (host === "youtube.com" || host === "youtu.be" || host === "m.youtube.com") {
        const vid = host === "youtu.be" ? u.pathname.slice(1) : (u.searchParams.get("v") || "");
        if (vid) return (
          <iframe
            src={`https://www.youtube.com/embed/${vid}`}
            title="Listing video"
            className="w-full h-full"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        );
      }
      if (host === "vimeo.com" || host === "player.vimeo.com") {
        const seg = u.pathname.split("/").filter(Boolean)[0] || "";
        if (seg) return (
          <iframe
            src={`https://player.vimeo.com/video/${seg}`}
            title="Listing video"
            className="w-full h-full"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        );
      }
    } catch { /* fall through to native player */ }
    return (
      <video src={url} controls playsInline preload="metadata" className="w-full h-full bg-black" />
    );
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-8">
      <Link to="/marketplace" className="text-xs font-mono text-gray-500 hover:text-[#B31E1E]" data-testid="back-to-marketplace">← Back to Marketplace</Link>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
        {/* Photo gallery */}
        <div>
          <div className="aspect-square rounded-md bg-gray-100 border border-border overflow-hidden">
            {photos[activePhoto] ? (
              <img src={photos[activePhoto]} alt={l.title} className="w-full h-full object-contain" data-testid="mp-active-photo" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">No photos</div>
            )}
          </div>
          {photos.length > 1 && (
            <div className="mt-2 flex gap-2 overflow-x-auto">
              {photos.map((p, i) => (
                <button
                  key={`thumb-${i}-${(p || "").slice(-16)}`}
                  onClick={() => setActivePhoto(i)}
                  className={`h-16 w-16 shrink-0 rounded-md overflow-hidden border ${i === activePhoto ? "border-[#B31E1E]" : "border-border"}`}
                  data-testid={`mp-thumb-${i}`}
                >
                  <img src={p} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {videos.length > 0 && (
            <div className="mt-4 space-y-3" data-testid="mp-videos-panel">
              <p className="text-[11px] uppercase tracking-wider font-mono text-[#5C4E48]">
                Video{videos.length > 1 ? "s" : ""} · see the item {videos.length > 1 ? "in action" : "in action or with defects"}
              </p>
              {videos.map((v, i) => (
                <div key={`video-${i}`} className="aspect-video rounded-md overflow-hidden border border-border bg-black" data-testid={`mp-video-${i}`}>
                  {renderVideo(v)}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="rounded-sm">{CATEGORY_META[l.category] || "Other"}</Badge>
            <Badge variant="outline" className="rounded-sm">{CONDITION_LABEL[l.condition] || "—"}</Badge>
            {l.seller_type === "business" ? (
              <Badge className="rounded-sm bg-[#B31E1E]"><Store size={10} className="mr-1" /> Business</Badge>
            ) : (
              <Badge variant="outline" className="rounded-sm"><UserIcon size={10} className="mr-1" /> Individual</Badge>
            )}
            {l.status === "sold" && <Badge className="rounded-sm bg-red-600">Sold</Badge>}
            {l.status === "reserved" && <Badge className="rounded-sm bg-amber-500">Reserved</Badge>}
          </div>

          <div className="mt-3 flex items-baseline gap-2">
            {l.listing_type === "auction" ? (
              <div className="font-display text-4xl">{formatPrice(l.current_bid_cents || l.starting_bid_cents, l.currency)}</div>
            ) : (
              <div className="font-display text-4xl">{formatPrice(l.price_cents, l.currency)}</div>
            )}
            {l.listing_type === "auction" && (
              <span className="text-xs text-gray-500">
                {l.current_bid_cents ? "current bid" : "starting bid"}
              </span>
            )}
            {l.negotiable && l.listing_type !== "auction" && <span className="text-xs text-gray-500">or best offer</span>}
            {l.accepts_offers && l.listing_type === "fixed" && !l.negotiable && (
              <span className="text-xs text-[#B31E1E] font-medium">accepts offers</span>
            )}
          </div>
          <h1 className="mt-2 font-display text-2xl leading-tight">{l.title}</h1>
          <div className="mt-2 text-sm text-gray-500 flex items-center gap-1">
            <MapPin size={12} /> {l.city}{l.state ? `, ${l.state}` : ""} {l.zip ? ` · ${l.zip}` : ""}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {DELIVERY_LABEL[l.delivery_mode] || "Pickup only"}
            {(l.delivery_mode === "shipping" || l.delivery_mode === "both") && l.shipping_cents > 0 && (
              <> · Shipping {formatPrice(l.shipping_cents, l.currency)}</>
            )}
          </div>

          <p className="mt-4 text-sm text-gray-800 whitespace-pre-wrap">{l.description}</p>

          {/* Auction bidding UI — countdown, bid form, bid history. Rendered
              only when listing_type === "auction". */}
          {l.listing_type === "auction" && (
            <div className="mt-6">
              <AuctionPanel listing={l} onUpdate={(fresh) => setL(fresh)} />
            </div>
          )}

          {/* Shipping-quote widget — buyer enters their ZIP and we hit
              /marketplace/listings/{lid}/shipping-quote for live carrier rates
              (EasyPost) or a built-in estimator when the API key isn't set. */}
          {(l.delivery_mode === "shipping" || l.delivery_mode === "both") && l.weight_oz > 0 && (
            <div className="mt-6">
              <ShippingQuoteWidget listing={l} />
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-2">
            {!isMine && l.status === "active" && l.listing_type !== "auction" && (
              <>
                {paymentsEnabled && l.price_cents > 0 && (
                  <Button
                    onClick={buyNow}
                    disabled={busy}
                    className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white"
                    data-testid="mp-buy-button"
                  >
                    <ShoppingCart size={14} className="mr-2" /> Buy now — {formatPrice(l.price_cents + (l.shipping_cents || 0), l.currency)}
                  </Button>
                )}
                <MakeOfferButton listing={l} />
                <Button
                  onClick={contact}
                  disabled={busy}
                  variant={paymentsEnabled && l.price_cents > 0 ? "outline" : "default"}
                  className={paymentsEnabled && l.price_cents > 0
                    ? "rounded-[35px]"
                    : "rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white"}
                  data-testid="mp-contact-button"
                >
                  <MessageCircle size={14} className="mr-2" /> Message seller
                </Button>
              </>
            )}
            {!isMine && l.status === "active" && l.listing_type === "auction" && (
              <Button
                onClick={contact}
                disabled={busy}
                variant="outline"
                className="rounded-[35px]"
                data-testid="mp-contact-button"
              >
                <MessageCircle size={14} className="mr-2" /> Message seller
              </Button>
            )}
            {isMine ? (
              <>
                <Link to={`/marketplace/sell/${l.id}`}>
                  <Button variant="outline" className="rounded-[35px]" data-testid="mp-edit-button">
                    <Edit3 size={13} className="mr-2" /> Edit
                  </Button>
                </Link>
                {l.status === "active" ? (
                  <Button variant="outline" className="rounded-[35px]" onClick={markSold} disabled={busy} data-testid="mp-mark-sold-button">
                    <Check size={13} className="mr-2" /> Mark sold
                  </Button>
                ) : (
                  <Button variant="outline" className="rounded-[35px]" onClick={relist} disabled={busy} data-testid="mp-relist-button">
                    <RefreshCcw size={13} className="mr-2" /> Relist
                  </Button>
                )}
                <Button variant="ghost" className="rounded-[35px] text-red-600 hover:text-red-700" onClick={del} disabled={busy} data-testid="mp-delete-button">
                  <Trash2 size={13} className="mr-2" /> Delete
                </Button>
              </>
            ) : (
              user && (
                <Button variant="ghost" className="rounded-[35px]" onClick={flag} disabled={busy} data-testid="mp-flag-button">
                  <Flag size={13} className="mr-2" /> Report
                </Button>
              )
            )}
          </div>

          <div className="mt-8 text-xs text-gray-500 border-t border-border pt-4">
            Sold by {l.seller_type === "business" ? (l.seller_business_name || l.seller_name) : l.seller_name}
            {l.seller_type === "business" && l.seller_is_pro && <Badge variant="outline" className="ml-2 rounded-sm text-[10px] border-[#B31E1E] text-[#B31E1E]">Servrevu Pro</Badge>}
            <div className="mt-1">Listed {new Date(l.created_at).toLocaleDateString()} · {l.view_count || 0} views</div>
          </div>
        </div>
      </div>
    </div>
  );
}
